export interface RepaymentInstallment {
  installmentNumber: number;
  dueDate: Date;
  installmentAmount: number;
  principalPortion: number;
  interestPortion: number;
}

export interface LoanScheduleParams {
  principal: number;
  annualRate: number;
  installments: number;
  disbursementDate: Date;
}

/**
 * Generates an amortised repayment schedule for a loan.
 * Uses EMI = P * r * (1+r)^n / ((1+r)^n - 1) for interest-bearing loans.
 * Falls back to P / n per installment when annualRate = 0.
 */
export function generateRepaymentSchedule({
  principal,
  annualRate,
  installments,
  disbursementDate,
}: LoanScheduleParams): RepaymentInstallment[] {
  const schedule: RepaymentInstallment[] = [];
  const monthlyRate = annualRate / 12;

  let emi: number;
  if (annualRate === 0) {
    emi = principal / installments;
  } else {
    const factor = Math.pow(1 + monthlyRate, installments);
    emi = (principal * monthlyRate * factor) / (factor - 1);
  }

  let remainingBalance = principal;

  for (let i = 1; i <= installments; i++) {
    const dueDate = new Date(disbursementDate);
    dueDate.setMonth(dueDate.getMonth() + i);

    const interestPortion = remainingBalance * monthlyRate;
    let principalPortion: number;
    let installmentAmount: number;

    if (i === installments) {
      // Last installment: clear remaining balance to avoid floating point drift
      principalPortion = remainingBalance;
      installmentAmount = principalPortion + interestPortion;
    } else {
      principalPortion = emi - interestPortion;
      installmentAmount = emi;
    }

    remainingBalance -= principalPortion;

    // Round principal and interest first, then derive installmentAmount from
    // the rounded values so that installmentAmount === principalPortion + interestPortion always holds.
    const roundedPrincipal = Math.round(principalPortion * 100) / 100;
    const roundedInterest = Math.round(interestPortion * 100) / 100;

    schedule.push({
      installmentNumber: i,
      dueDate,
      installmentAmount: Math.round((roundedPrincipal + roundedInterest) * 100) / 100,
      principalPortion: roundedPrincipal,
      interestPortion: roundedInterest,
    });
  }

  return schedule;
}

// ---------------------------------------------------------------------------
// Helpers used by property tests (Properties 9, 10, 11, 12)
// ---------------------------------------------------------------------------

export interface InstallmentStatus {
  installmentNumber: number;
  installmentAmount: number;
  principalPortion: number;
  interestPortion: number;
  isPaid: boolean;
}

/**
 * Counts paid and remaining installments from a schedule with paid flags.
 * Property 9: paid_count + remaining_count === total
 */
export function countInstallments(schedule: InstallmentStatus[]): {
  paidCount: number;
  remainingCount: number;
  total: number;
} {
  const paidCount = schedule.filter((r) => r.isPaid).length;
  return { paidCount, remainingCount: schedule.length - paidCount, total: schedule.length };
}

/**
 * Returns true when installment at index `k` (0-based) is being marked paid
 * but at least one installment with a lower installment number is still unpaid.
 * Property 10: out-of-sequence detection
 */
export function isOutOfSequence(schedule: InstallmentStatus[], targetIndex: number): boolean {
  const target = schedule[targetIndex];
  if (!target) return false;
  return schedule.some(
    (r) => r.installmentNumber < target.installmentNumber && !r.isPaid
  );
}

/**
 * Returns 'closed' when every installment is paid, otherwise 'active'.
 * Property 11: loan auto-closes when all installments are paid
 */
export function computeLoanStatus(schedule: InstallmentStatus[]): 'active' | 'closed' {
  if (schedule.length === 0) return 'active';
  return schedule.every((r) => r.isPaid) ? 'closed' : 'active';
}

/**
 * Computes outstanding balance (sum of installment_amount for unpaid rows)
 * and total interest paid (sum of interest_portion for paid rows).
 * Property 12: outstanding balance and interest paid computation
 */
export function computeBalanceMetrics(schedule: InstallmentStatus[]): {
  outstandingBalance: number;
  totalInterestPaid: number;
  totalPrincipalPaid: number;
} {
  let outstandingBalance = 0;
  let totalInterestPaid = 0;
  let totalPrincipalPaid = 0;
  for (const r of schedule) {
    if (r.isPaid) {
      totalInterestPaid += r.interestPortion;
      totalPrincipalPaid += r.principalPortion;
    } else {
      outstandingBalance += r.installmentAmount;
    }
  }
  return { outstandingBalance, totalInterestPaid, totalPrincipalPaid };
}
